import { useState, useEffect, useCallback } from 'react'

export interface ColDef { readonly id: string; readonly label: string }

interface StoredPrefs { order: string[]; hidden: string[] }

// Z77 revised: a localStorage-backed column order/visibility hook -- deliberately mirrors the
// external shape of the DB-backed useColumnPrefs (src/pages/Warehouse/useColumnPrefs.ts) closely
// enough to drive the exact same SortableColHeader/ColumnToggleButton UI (src/pages/Warehouse/
// ColDragHeader.tsx) without modification, but persists per-browser via localStorage rather than
// per-user-across-devices via Supabase -- Gary asked for localStorage specifically here, not the
// existing DB-backed pattern. Generic/table-agnostic like its DB-backed counterpart: any page can
// reuse this by passing its own storageKey + column defs.
export function useLocalColumnPrefs(storageKey: string, defaultCols: readonly ColDef[]) {
  const defaultOrder = defaultCols.map(c => c.id)
  const [order, setOrderState] = useState<string[]>(defaultOrder)
  const [hidden, setHiddenState] = useState<string[]>([])
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey)
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<StoredPrefs>
        const validIds = new Set(defaultOrder)
        const savedOrder = Array.isArray(parsed.order) ? parsed.order.filter(id => validIds.has(id)) : []
        // A column added to defaultCols after prefs were last saved (or nothing saved yet) gets
        // appended at the end, rather than silently disappearing from the table.
        const merged = [...savedOrder, ...defaultOrder.filter(id => !savedOrder.includes(id))]
        setOrderState(merged)
        if (Array.isArray(parsed.hidden)) setHiddenState(parsed.hidden.filter(id => validIds.has(id)))
      }
    } catch {
      // Corrupt or blocked localStorage (private window, cleared site data) -- fall back to
      // defaults silently, never break the page over a column-preference read.
    }
    setLoaded(true)
    // Deliberately only re-runs if the key itself changes -- defaultCols is expected to be a
    // stable module-level constant, same convention as useColumnPrefs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey])

  const persist = useCallback((next: StoredPrefs) => {
    try { localStorage.setItem(storageKey, JSON.stringify(next)) } catch {
      // Ignore write failures (private window, storage full/blocked) -- state still updates in
      // memory for this session, it just won't survive a reload.
    }
  }, [storageKey])

  const setOrder = useCallback((newOrder: string[]) => {
    setOrderState(newOrder)
    persist({ order: newOrder, hidden })
  }, [hidden, persist])

  const toggleHidden = useCallback((id: string) => {
    setHiddenState(prev => {
      const next = prev.includes(id) ? prev.filter(h => h !== id) : [...prev, id]
      persist({ order, hidden: next })
      return next
    })
  }, [order, persist])

  const reset = useCallback(() => {
    setOrderState(defaultOrder)
    setHiddenState([])
    try { localStorage.removeItem(storageKey) } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey])

  const colById = new Map(defaultCols.map(c => [c.id, c]))
  const visibleCols = order.filter(id => !hidden.includes(id)).map(id => colById.get(id)).filter((c): c is ColDef => !!c)

  return { order, hidden, visibleCols, loaded, setOrder, toggleHidden, reset }
}
